import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextareaModule } from "primeng/inputtextarea";
import { ToastModule } from "primeng/toast";
import { MessageService } from "primeng/api";
import { forkJoin } from "rxjs";
import { AuthenticationService } from "../../services/authentication.service";
import { PermissionEnum } from "../../model/permission-enum";
import { CourtReservationService } from "../../services/court-reservation.service";
import { CourtService } from "../../services/court.service";
import { UserService } from "../../services/user.service";
import { ClubService } from "../../services/club.service";

@Component({
  selector: "app-system-alerts",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextareaModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: "./system-alerts.component.html",
  styleUrl: "./system-alerts.component.css",
})
export class SystemAlertsComponent implements OnInit {
  reservations: any[] = [];
  loading = false;
  accessDenied = false;
  startDate: Date = new Date();
  endDate: Date = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  clubId: number | null = null;
  clubName: string = "";
  courtMap: { [key: number]: string } = {};
  bookerPhoneMap: { [key: number]: string } = {};
  vipIds: Set<number> = new Set<number>();

  denyDialogVisible = false;
  denyReason = "";
  reservationToDeny: any | null = null;
  playersDialogVisible = false;
  players: { id: number; full_name: string; position: number; isVip: boolean }[] = [];

  onStartDateChange(value: string): void {
    this.startDate = new Date(value);
  }

  onEndDateChange(value: string): void {
    this.endDate = new Date(value);
  }

  constructor(
    private authService: AuthenticationService,
    private reservationService: CourtReservationService,
    private courtService: CourtService,
    private messageService: MessageService,
    private userService: UserService,
    private clubService: ClubService
  ) {}

  ngOnInit(): void {
    const current = this.authService.currentUserValue;
    let permissions = this.authService.currentUserPermissions;
    let hasAdminPermission = permissions.some(
      (permission: PermissionEnum) => permission == PermissionEnum.Admin
    );
    if (!current || !hasAdminPermission) {
      this.accessDenied = true;
      return;
    }

    this.userService.getUser(current.id).subscribe({
      next: (u) => {
        this.clubId = u.club?.id ?? u.club_id;
        this.clubName = u.club?.name ?? "";
        if (this.clubId) {
          this.loadCourts();
          this.loadVipPlayers();
          this.loadReservations();
        }
      },
      error: () => {
        this.accessDenied = true;
      },
    });
  }

  loadCourts(): void {
    if (!this.clubId) return;
    this.courtService.getCourtsForClub(this.clubId).subscribe((courts) => {
      this.courtMap = {};
      courts.forEach((c: any) => (this.courtMap[c.id] = c.name));
    });
  }

  loadVipPlayers(): void {
    if (!this.clubId) return;
    this.clubService.getVipUsers(this.clubId).subscribe((users) => {
      this.vipIds = new Set(users.map((u: any) => u.id));
    });
  }

  loadReservations(): void {
    if (!this.clubId) return;
    this.loading = true;
    const start = this.startDate.toDateString();
    const end = this.endDate.toDateString();
    const statuses = ["Pending", "Approved", "Denied"];
    forkJoin(
      statuses.map((s) =>
        this.reservationService.getReservationsForClubDateRange(
          this.clubId!,
          start,
          end,
          s
        )
      )
    ).subscribe({
      next: (result) => {
        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);
        this.reservations = result
          .flat()
          .filter((r) => r.club_id === this.clubId)
          .filter((r) => {
            const resDate = new Date(r.reservation_start_time);
            return resDate >= startDate && resDate <= endDate;
          });
        this.populateUsersAndPhones();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load reservations",
        });
      },
    });
  }

  private populateUsersAndPhones(): void {
    const pairs = this.reservations.map((r) => ({
      resId: r.id,
      userId: r.user_id,
      bookerId: this.getBookerId(r),
    }));
    const ids = Array.from(
      new Set(
        pairs
          .map((p) => [p.userId, p.bookerId])
          .flat()
          .filter((id): id is number => id != null)
      )
    );
    if (!ids.length) {
      return;
    }
    forkJoin(ids.map((id) => this.userService.getUser(id))).subscribe(
      (users) => {
        const map: { [key: number]: any } = {};
        users.forEach((u) => (map[u.id] = u));
        this.reservations.forEach((r) => {
          if (!r.user && r.user_id && map[r.user_id]) {
            r.user = map[r.user_id];
          }
          const bookerId = this.getBookerId(r);
          if (bookerId && map[bookerId]) {
            const user = map[bookerId];
            this.bookerPhoneMap[r.id] =
              user.phone_number || user.phone || user.phoneNumber || "";
          }
        });
      }
    );
  }

  getBookerId(res: any): number | null {
    if (this.getBookType(res) === "admin") {
      return (
        res.courtReservationPlayer?.find((p: any) => p.position === 0)?.player_id ||
        null
      );
    }
    return res.user_id || null;
  }

  approveReservation(res: any): void {
    this.reservationService
      .updateCourtReservation({ ...res, reservation_status: "Approved" })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Reservation approved",
          });
          this.loadReservations();
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Action failed",
          });
        },
      });
  }

  openDenyDialog(res: any): void {
    this.reservationToDeny = res;
    this.denyReason = "";
    this.denyDialogVisible = true;
  }

  submitDeny(): void {
    if (!this.reservationToDeny) return;
    this.reservationService
      .updateCourtReservation({
        ...this.reservationToDeny,
        reservation_status: "Denied",
        deny_reason: this.denyReason,
      })
      .subscribe({
        next: () => {
          const id = this.reservationToDeny?.id;
          if (id) {
            this.reservationService.deleteCourtReservation(id).subscribe({
              next: () => {
                this.messageService.add({
                  severity: "success",
                  summary: "Success",
                  detail: "Reservation denied",
                });
                this.denyDialogVisible = false;
                this.loadReservations();
              },
              error: () => {
                this.messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: "Action failed",
                });
              },
            });
          } else {
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Reservation denied",
            });
            this.denyDialogVisible = false;
            this.loadReservations();
          }
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Action failed",
          });
        },
      });
  }

  showPlayers(res: any): void {
    this.players =
      res.courtReservationPlayer
        ?.map((p: any) => ({
          id: p.player.id,
          full_name: p.player.full_name,
          position: p.position,
          isVip: this.vipIds.has(p.player.id),
        }))
        .sort((a: any, b: any) => a.position - b.position) || [];
    this.playersDialogVisible = true;
  }

  getPositionLabel(pos: number): string {
    switch (pos) {
      case 0:
        return "Booker";
      case 1:
        return "Booker's teammate";
      case 3:
        return "Opponent";
      case 4:
        return "Opponent (team 2)";
      default:
        return `Player ${pos}`;
    }
  }

  getMatchType(res: any): string {
    const playerCount = res.courtReservationPlayer?.length || 0;
    return playerCount === 2 ? "Single" : "Double";
  }

  getBookType(res: any): string {
    const players = res.courtReservationPlayer?.map((p: any) => p.player_id) || [];
    return players.includes(res.user_id) ? "player" : "admin";
  }

  getBookerPhone(res: any): string {
    return this.bookerPhoneMap[res.id] || "";
  }

  isVip(id: number | null): boolean {
    return id !== null && this.vipIds.has(id);
  }

  getUserName(res: any): string {
    if (this.getBookType(res) === "admin") {
      const booker = res.courtReservationPlayer?.find((p: any) => p.position === 0)?.player;
      if (booker) {
        if (booker.full_name) {
          return booker.full_name;
        }
        const parts: string[] = [];
        if (booker.name) {
          parts.push(booker.name);
        }
        if (booker.last_name) {
          parts.push(booker.last_name);
        }
        if (parts.length) {
          return parts.join(" ");
        }
        if (booker.username) {
          return booker.username;
        }
      }
    }
    if (!res.user) {
      return "";
    }
    if (res.user.full_name) {
      return res.user.full_name;
    }
    const parts: string[] = [];
    if (res.user.name) {
      parts.push(res.user.name);
    }
    if (res.user.last_name) {
      parts.push(res.user.last_name);
    }
    if (parts.length) {
      return parts.join(" ");
    }
    if (res.user.username) {
      return res.user.username;
    }
    return "";
  }
}
